"use client"

/* ds-typo-ignore-file — EN-only prototype ad LP: intentional sub-DS sizes (not shipped DS surfaces) */

import Link from "next/link"
import Image from "next/image"
import { useTranslations } from "next-intl"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { blobMediaUrl } from "@/lib/blob-url"
import { goKeys as k } from "@/i18n/go-keys"
import { Button, buttonVariants, ArrowRightIcon } from "@/components/ui/button"
import { CheckCircleIcon } from "@/components/ui/icons"
import { ReportMockupIpad } from "@/components/ui/report-mockup-ipad"
import { BloodTrackingComparison } from "@/components/ui/blood-tracking-comparison"
import { ImmuneStepsTimeline } from "@/components/ui/immune-steps-timeline"
import { MARKERS, PROCESS } from "./heart-health-data"
import {
  SECTION,
  BookingLink,
  MeasureMarkerCard,
  CtaLabel,
  CtaReassurance,
  useBookingHref,
} from "./heart-health-components"

/* ── 8. How it works — scroll-linked stepper (concept from PR #1309) ── */
export function HeartHealthProcess({
  bloodDrawCount,
  showImaging,
}: {
  /** Live count of active blood-draw labs, interpolated into step 1. */
  bloodDrawCount: number
  /** Blood-only markets drop the optional imaging visit. */
  showImaging: boolean
}) {
  const t = useTranslations("go")
  const bookingHref = useBookingHref()
  // Step 1 advertises the live number of blood-draw labs from the CMS. The copy
  // stays in the message catalog with a non-ICU `[[count]]` sentinel (a real
  // `{count}` placeholder would force the shared "go" namespace to require
  // interpolation args on every generic t() call), interpolated here.
  // Dropping the imaging step renumbers the rest for free — the timeline
  // numbers steps by position.
  const steps = PROCESS.filter((s) => showImaging || !("imaging" in s)).map(
    (s, i) =>
      i === 0
        ? {
            ...s,
            desc: t(k.heartHealthProcessStep1Desc).replace(
              "[[count]]",
              String(bloodDrawCount)
            ),
          }
        : s
  )
  return (
    <ImmuneStepsTimeline
      label={t(k.heartHealthProcessLabel)}
      headingKey={k.heartHealthProcessHeading}
      bodyKey={k.heartHealthProcessBody}
      stepWord={t(k.heartHealthProcessStepWord)}
      steps={steps}
      namespace="go"
      cta={
        <div className="flex flex-col items-center gap-6">
          <BookingLink
            href={bookingHref}
            location="heart-health-how-it-works"
            className="block w-full sm:inline-block sm:w-auto"
          >
            <Button
              size="lg"
              variant="primary"
              className="group w-full sm:w-auto"
              iconRight={
                <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
              }
            >
              <CtaLabel label={t(k.heartHealthHeroCta1)} />
            </Button>
          </BookingLink>
          <CtaReassurance />
        </div>
      }
    />
  )
}

/* ── 5. What we measure — the four groups, in the health-risks card design
    (photo tile + icon · category · title · description · biomarker chips). ── */
export function HeartHealthMeasure({ showImaging }: { showImaging: boolean }) {
  const t = useTranslations("go")
  const lp = useLocalePath()
  const bookingHref = useBookingHref()
  return (
    <section className="bg-white py-16 pb-28 md:py-24 md:pb-32">
      <div className={SECTION}>
        <div className="mx-auto flex max-w-[677px] flex-col items-center gap-4 text-center">
          <p className="type-label-md text-neutral-600 uppercase">
            {t(k.heartHealthMeasureEyebrow)}
          </p>
          <h2 className="type-display-md text-neutral-900">
            {t(k.heartHealthMeasureHeading)}
          </h2>
          <p className="type-body-xl max-w-[520px] text-neutral-500">
            {t(k.heartHealthMeasureBody)}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {MARKERS.map((m) => (
            <MeasureMarkerCard key={m.titleKey} marker={m} t={t} />
          ))}
        </div>

        {/* Related indexable product pages — SEO interlinking to the dedicated
            Lp(a) and coronary calcium-score tests (they feed this LP and vice
            versa). Plain text links, locale-prefixed via useLocalePath. */}
        <p className="type-body-md mt-8 text-center text-neutral-500">
          {t(k.heartHealthRelatedLabel)}{" "}
          <Link
            href={lp("/tests/lpa")}
            className="text-neutral-900 underline decoration-neutral-300 underline-offset-4 transition-colors hover:decoration-neutral-900"
          >
            {t(k.heartHealthRelatedLpa)}
          </Link>
          {/* The calcium-score cross-link only exists where that scan is
              sellable — otherwise it points at a product this market can't
              book. The Lp(a) link is blood, so it always stays. */}
          {showImaging && (
            <>
              {" · "}
              <Link
                href={lp("/tests/calcium-score")}
                className="text-neutral-900 underline decoration-neutral-300 underline-offset-4 transition-colors hover:decoration-neutral-900"
              >
                {t(k.heartHealthRelatedCalcium)}
              </Link>
            </>
          )}
        </p>

        <div className="mt-14 flex flex-col items-center gap-6">
          <BookingLink
            href={bookingHref}
            location="heart-health-measure"
            className="block w-full sm:inline-block sm:w-auto"
          >
            <Button
              size="lg"
              variant="primary"
              className="group w-full sm:w-auto"
              iconRight={
                <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
              }
            >
              <CtaLabel label={t(k.heartHealthHeroCta1)} />
            </Button>
          </BookingLink>
          <CtaReassurance />
        </div>
      </div>
    </section>
  )
}

/* ── 7d. Your report (visual anchor — wire real ReportMockupIpad later) ── */
export function HeartHealthReport() {
  const t = useTranslations("go")
  const lp = useLocalePath()
  return (
    <section className="pt-2 pb-6 md:pt-4 md:pb-8">
      <div className={SECTION}>
        <div className="relative overflow-hidden rounded-[32px] bg-white">
          <div className="grid items-stretch md:grid-cols-[0.82fr_1.18fr]">
            <div className="flex flex-col justify-center px-8 py-12 md:px-14 md:py-20 lg:px-16">
              <p className="type-label-md text-neutral-600 uppercase">
                {t(k.heartHealthReportEyebrow)}
              </p>
              <h2 className="type-display-md mt-3 max-w-[420px] text-neutral-900">
                {t(k.heartHealthReportHeading)}
              </h2>
              <p className="type-body-lg mt-5 max-w-[460px] text-neutral-600">
                {t(k.heartHealthReportBody)}
              </p>
              <div className="mt-10">
                <Link
                  href={lp("/report")}
                  className="block w-full sm:inline-block sm:w-auto"
                >
                  <Button variant="tonal" className="w-full sm:w-auto">
                    {t(k.heartHealthReportCta)}
                  </Button>
                </Link>
              </div>
            </div>
            {/* right — iPad report mockup, bleeds into the panel's right + bottom edges (cropped flush, no padding) */}
            <div className="relative min-h-[440px] md:min-h-[600px]">
              <div className="absolute top-10 left-0 w-[135%] md:top-14">
                <ReportMockupIpad />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ── 9c. Why not just my GP? — "what you get back" report comparison, reusing
    the two-column layout from /compare-health-check-ups-switzerland: a white
    highlighted "Ahead" card (green checks + CTA + report mockup) beside a muted
    "Typical" column (✗ rows + a plain paper report). Placed before pricing as
    the last objection-handler. Prototype copy, hardcoded EN. ── */
const GP_AHEAD_ROW_KEYS = [
  k.heartHealthGpAheadRow1,
  k.heartHealthGpAheadRow2,
  k.heartHealthGpAheadRow3,
  k.heartHealthGpAheadRow4,
  k.heartHealthGpAheadRow5,
  k.heartHealthGpAheadRow6,
] as const

const GP_TYPICAL_ROW_KEYS = [
  k.heartHealthGpTypicalRow1,
  k.heartHealthGpTypicalRow2,
  k.heartHealthGpTypicalRow3,
  k.heartHealthGpTypicalRow4,
  k.heartHealthGpTypicalRow5,
  k.heartHealthGpTypicalRow6,
] as const

export function HeartHealthGpCompare({
  showImaging,
}: {
  /** Blood-only markets drop the imaging row from BOTH columns, so the two
   *  lists stay row-for-row aligned. */
  showImaging: boolean
}) {
  const t = useTranslations("go")
  const aheadRowKeys = showImaging
    ? GP_AHEAD_ROW_KEYS
    : GP_AHEAD_ROW_KEYS.filter((key) => key !== k.heartHealthGpAheadRow5)
  const typicalRowKeys = showImaging
    ? GP_TYPICAL_ROW_KEYS
    : GP_TYPICAL_ROW_KEYS.filter((key) => key !== k.heartHealthGpTypicalRow5)
  return (
    <section className="bg-neutral-50 py-16 md:py-24">
      <div className={SECTION}>
        <h2 className="type-display-md mx-auto max-w-[820px] text-center text-neutral-900">
          {t(k.heartHealthGpHeading)}
        </h2>
        <p className="type-body-xl mx-auto mt-4 max-w-[560px] text-center text-neutral-500">
          {t(k.heartHealthGpBody)}
        </p>

        {/* Gray panel — white highlighted "Ahead" card beside the muted
            "Typical" column, which sits in a matching (invisible) footprint so
            the two read as a balanced pair. Both column contents are vertically
            centred within the equal-height cells. */}
        <div className="mx-auto mt-12 max-w-[1100px] rounded-[32px] bg-neutral-100 p-4 md:p-6">
          <div className="grid items-stretch gap-4 md:grid-cols-2 md:gap-6">
            {/* left — Ahead, highlighted white card */}
            <div className="flex flex-col justify-center rounded-[24px] bg-white px-7 py-10 md:px-10 md:py-12">
              <h3 className="type-title-lg text-neutral-900">
                {t(k.heartHealthGpAheadTitle)}
              </h3>
              <ul className="mt-6 flex flex-col gap-4">
                {aheadRowKeys.map((rowKey) => (
                  <li key={rowKey} className="flex items-center gap-3">
                    <CheckCircleIcon size={22} />
                    <span className="type-body-lg text-neutral-900">
                      {t(rowKey)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* right — Typical, centred in a matching invisible-card footprint */}
            <div className="flex flex-col justify-center px-7 py-10 md:px-10 md:py-12">
              <h3 className="type-title-lg text-neutral-700">
                {t(k.heartHealthGpTypicalTitle)}
              </h3>
              <ul className="mt-6 flex flex-col gap-4">
                {typicalRowKeys.map((rowKey) => (
                  <li key={rowKey} className="flex items-center gap-3">
                    {/* long muted dash, aligned to the check's slot */}
                    <span
                      aria-hidden
                      className="h-0.5 w-[22px] shrink-0 rounded-full bg-neutral-300"
                    />
                    <span className="type-body-lg text-neutral-500">
                      {t(rowKey)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ── 10. The offer — two market states, both on the same dark section:
    1. imaging sellable → blood test vs blood + coronary calcium score
       (BloodTrackingComparison, blood-mri variant). The imaging arm here is the
       coronary CALCIUM SCORE (a low-dose cardiac CT add-on), NOT the full-body
       MRI, so the upgrade card's copy is overridden accordingly.
    2. not sellable → the standalone one-off blood card, carrying the primary
       CTA. No membership fork: membership rides the same EXPANSION flag as
       imaging, so a market without one has neither.
    Prices render live from lib/pricing.ts. ── */
export function HeartHealthPricing({
  showImaging,
}: {
  /** Where the imaging arm isn't sellable the upgrade card is dropped entirely
   *  (not shown as coming-soon). The heading + body swap to copy that doesn't
   *  promise a second option. */
  showImaging: boolean
}) {
  const t = useTranslations("go")
  const lp = useLocalePath()
  return (
    <section
      id="pricing"
      className="bg-deep-blue-950 relative scroll-mt-24 overflow-hidden text-white"
    >
      {/* Mountain-range backdrop — slightly blurred, with a neutral dark
          shade over it so the white heading + cards stay legible. */}
      <div aria-hidden className="absolute inset-0">
        <Image
          src={blobMediaUrl("/images/metabolism/pricing-mountain-range.webp")}
          alt=""
          fill
          sizes="100vw"
          className="scale-105 object-cover object-center blur-sm"
        />
        <div className="absolute inset-0 bg-neutral-950/60" />
      </div>
      <div className="relative z-10 mx-auto max-w-[1100px] px-6 py-20 md:px-10 md:py-28">
        <div className="mx-auto max-w-[720px] text-center">
          <h2 className="type-display-md text-white">
            {t(
              showImaging
                ? k.heartHealthPricingHeading
                : k.heartHealthPricingHeadingBloodOnly
            )}
          </h2>
          <p className="type-body-lg mx-auto mt-5 max-w-[560px] text-white/60">
            {t(
              showImaging
                ? k.heartHealthPricingBody
                : k.heartHealthPricingBodyBloodOnly
            )}
          </p>
        </div>

        {/* Immunity pricing component (BloodTrackingComparison) in its
            blood-vs-imaging variant: the white baseline "blood test" card + the
            highlighted "blood test + coronary calcium score" card. The upgrade
            card copy is overridden to the calcium score (CT add-on) so it never
            mislabels the scan as an MRI. CTA routes to the calcium-score page. */}
        <div className="mt-14">
          {showImaging ? (
            <BloodTrackingComparison
              offer="blood-mri"
              trackingLocation="go/heart-health"
              baselineCtaLabel={t(k.heartHealthPricingBaselineCta)}
              mriHref={lp("/tests/calcium-score")}
              mriPriceFrom={t(k.heartHealthPricingMriPriceFrom)}
              mriTitle={t(k.heartHealthPricingMriCardTitle)}
              mriBadge={t(k.heartHealthPricingMriCardBadge)}
              mriTagline={t(k.heartHealthPricingMriCardTagline)}
              mriPriceLabel={t(k.heartHealthPricingMriCardPriceLabel)}
              mriCtaLabel={t(k.heartHealthPricingMriCardCta)}
              mriRows={[
                t(k.heartHealthPricingMriRow1),
                t(k.heartHealthPricingMriRow2),
                t(k.heartHealthPricingMriRow3),
                t(k.heartHealthPricingMriRow4),
              ]}
            />
          ) : (
            /* Blood-only market with no membership either: the standalone
               one-off blood card, the same single-card path NL/DE render. */
            <BloodTrackingComparison
              showMembership={false}
              /* Two shared rows alone make a thin card. These two are already
                 translated on this page and are true of the one-off test. */
              extraBaselineRows={[
                t(k.heartHealthGpAheadRow6),
                t(k.heartHealthGpAheadRow1),
              ]}
              trackingLocation="go/heart-health"
            />
          )}
        </div>

        <p className="mx-auto mt-10 max-w-[680px] text-center text-base leading-relaxed font-medium text-white/70">
          {t(k.heartHealthPricingFootnote)}
        </p>
      </div>
    </section>
  )
}

/* ── 13. Closing CTA — simple heart-focused call to action over a
    full-bleed photo. Single booking CTA, no membership. ── */
export function HeartHealthClosingCta() {
  const t = useTranslations("go")
  const bookingHref = useBookingHref()
  return (
    <section className="relative isolate flex min-h-[620px] items-center overflow-hidden text-white md:min-h-[720px]">
      <Image
        src={blobMediaUrl("/images/metabolism/closing-cta-metabolism.webp")}
        alt=""
        aria-hidden
        fill
        sizes="100vw"
        className="object-cover"
      />
      {/* dark scrim keeps the white copy legible over the photo */}
      <div aria-hidden className="absolute inset-0 bg-neutral-950/70" />
      <div
        className={`${SECTION} relative z-10 w-full py-20 text-center md:py-28`}
      >
        <p className="type-label-md text-white/60 uppercase">
          {t(k.heartHealthCtaEyebrow)}
        </p>
        <h2 className="type-display-md mx-auto mt-5 max-w-[680px] text-white">
          {t(k.heartHealthCtaHeading)}
        </h2>
        <p className="type-body-lg mx-auto mt-5 max-w-[520px] text-white/70">
          {t(k.heartHealthCtaBody)}
        </p>
        <div className="mt-10 flex flex-col items-center gap-6">
          <BookingLink
            href={bookingHref}
            location="heart-health-bottom"
            className={`${buttonVariants({ variant: "primary" })} w-full justify-center sm:w-auto`}
          >
            <CtaLabel label={t(k.heartHealthCtaCta1)} />
          </BookingLink>
          <CtaReassurance tone="onDark" />
        </div>
      </div>
    </section>
  )
}
